import {
  COOKIE_FCP_ORDER_ID,
  COOKIE_PURCHASED_CART_ID,
  getCookie,
  getUserId,
  getValueFromPartnerCookie,
  setCookie
} from '@ecomm/shared-cookies'
import {
  type TrackingData,
  brazeSetCustomUserAttributes,
  setBrazeAttributeGoogleClientId,
  useFacebookTrackProductPurchaseJotai,
  useGoogleAnalytics,
  useLeadGenCaptureV2,
  useTrackPaymentConfirmation,
  useTrackTransactionEvent,
  visitorIdAtAt
} from '@ecomm/tracking'
import { cookiesOption } from '@simplisafe/ss-ecomm-data/simplisafe/yodaClient'
import { useCallback, useEffect } from 'react'

import { trackIdentify } from '@ecomm/cdp-tracking'
import {
  useCartLineItems,
  useCartShippingAddress,
  useCartState
} from '@ecomm/data-cart'
import { useLocale } from '@ecomm/data-hooks'
import { getCartId } from '@ecomm/data-storage'
import { useIdentify } from '@ecomm/shared-ninetailed'
import type { PaymentState } from '../usePayment/usePayment'
import { leadCapture } from './callback'

/**
 * Tracks various purchase events upon payment completion.
 */
export const useTrackPurchaseCompleteJotai = (
  paymentState: PaymentState,
  selectedPaymentOption: TrackingData['selectedPaymentOption']
) => {
  const cartState = useCartState()
  const shippingAddress = useCartShippingAddress()
  const lineItems = useCartLineItems()
  const locale = useLocale()
  const [clientId] = useGoogleAnalytics()

  const trackPaymentConfirmation = useTrackPaymentConfirmation()
  const trackTransactionEvent = useTrackTransactionEvent()
  const facebookTrackProductPurchase = useFacebookTrackProductPurchaseJotai()
  const identify = useIdentify()
  const leadGenCaptureV2 = useLeadGenCaptureV2()

  const trackPurchase = useCallback(() => {
    const email = shippingAddress?.email ?? ''
    const phone = shippingAddress?.phone ?? ''
    const firstName = shippingAddress?.firstName ?? ''
    const lastName = shippingAddress?.lastName ?? ''
    const hasSystem = lineItems.some(item => item.isCustomSystem)
    const visitorId = visitorIdAtAt()

    leadCapture(email)
    // set google client id for UK as custom braze attribute ECP-12023
    locale === 'en-GB' && clientId && setBrazeAttributeGoogleClientId(clientId)
    const partnerName = getValueFromPartnerCookie('partnerName')
    partnerName && brazeSetCustomUserAttributes({ 'Partner Name': partnerName })

    leadGenCaptureV2({
      source: 'purchase',
      siteMetadata: { sourceType: 'purchase' },
      email: email
    })

    const cartId = getCartId()
    cartId && setCookie(COOKIE_PURCHASED_CART_ID, cartId, cookiesOption)

    const orderId = getCookie<string>(COOKIE_FCP_ORDER_ID)
    const uid = getUserId()

    trackPaymentConfirmation(selectedPaymentOption, orderId)
    trackTransactionEvent(selectedPaymentOption, orderId, email)
    facebookTrackProductPurchase(orderId)
    // Ninetailed events
    identify({
      email: email,
      phone: phone,
      firstName: firstName,
      lastName: lastName,
      hasSystem: hasSystem,
      hasPurchased: true
    })
    // Rudderstack identify
    visitorId &&
      trackIdentify(visitorId, {
        email,
        phone,
        firstName,
        lastName,
        uid,
        shippingAddress
      })
  }, [
    clientId,
    facebookTrackProductPurchase,
    identify,
    leadGenCaptureV2,
    lineItems,
    locale,
    selectedPaymentOption,
    shippingAddress?.email,
    shippingAddress?.firstName,
    shippingAddress?.lastName,
    shippingAddress?.phone,
    trackPaymentConfirmation,
    trackTransactionEvent,
    trackIdentify
  ])

  useEffect(() => {
    cartState === 'with_items' && paymentState === 'complete' && trackPurchase()
  }, [cartState, paymentState])
}
